import React, {useEffect, useState} from 'react';
import { Field, useField, useForm } from 'react-final-form';
import MuiButton from "@material-ui/core/Button";

import RadioButton from './RadioBig';
import Grid from "@material-ui/core/Grid";
import Select from "./Select";
import TextInput from "./TextInput";
import styled from "styled-components";
import SelectCard from "./SelectCard";
import {getCookieValue} from "../utils/getCookieValue";

import { Trans } from "../utils/i18n";
import HelpModal from "./HelpModal";
import {useFormState, useFormStateDispatch} from "../FormComponents/FormState";
import {CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import dayjs from "dayjs";
import {usePage} from "@inertiajs/inertia-react";
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import {last} from "lodash";
import { useMutation } from '@tanstack/react-query';

const ProductItem = styled.div`
    transition: background-color .2s,color .2s;
    //border: 2px solid ${props => props.theme.gray};
    border-radius: 5px;
`;

const MarginTop = styled.div`
  margin-top: 15px;
`

const SerialNumber = React.memo(function ({ doNotShowConfirmDevice, setAddress, values, addCompany, addCoverageId, setAddressId, nextStep, validateSerialNumber, uppercase, setSerialnumber, shopMode, onChangeCallback}) {
    const orderEntryDispatch = useFormStateDispatch();
    const { categories } = useFormState();
    const form = useForm();
    const { serialNumber: {product, success, address, order, problems, accessories, openOrders, }, selectedProduct, selectedCategory, manualDeviceSelection} = useFormState();
    const [internalError, setError] = useState(false);
    const [helpLink, setHelpLink] = useState('');
    const [helpLinkText, setHelpLinkText] = useState('');
    const { dsn, user, serialnumberRegex, } = usePage().props;
    const field = useField("serialnumber");
    const { mutate: checkSerialnumber, isLoading: isCheckSerialnumberLoading } = useMutation({
        mutationFn: ({serialnumber}) => {
            return fetch(route('order.serialnumber'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN'),
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                    serialnumber,
                    type: values.requesttype,
                    selectedProductId: selectedProduct?.id || values?.configurationProduct?.id || undefined,
                    problem_1: values.problem_1,
                    problem_2: values.problem_2,
                    pop_date: values.pop_date,
                })
            })
                .then(response => response.json())
                .then(data => {
                    if(!data.success) {
                        throw data;
                    }

                    // TODO: We should remove such flags and enable cross-component communication
                    if (shopMode && data?.product?.id) {
                        if (data?.product?.productcategory?.id) {
                            form.change("productcategory", data.product.productcategory.id.toString());
                        }

                        if (data?.product?.model_group) {
                            form.change("model_group", data.product.model_group);
                        }

                        if (data?.product?.productVariants?.length) {
                            data?.product?.productVariants?.forEach((productVariant, index) => {
                                if (!productVariant.productVariantOption?.key) return;
                                form.change(`variant.${productVariant.productVariantOption.key}`, productVariant.value);
                            });
                        }
                    }

                    orderEntryDispatch({ type: 'setSerialNumber', value: data });
                    form.change('warranty', data.warranty);
                    form.change('warrantyType', data.warrantyType);
                    setError(false);
                })
                .catch((errorResponse) => {

                    orderEntryDispatch({ type: 'setSerialNumber', value: {} });
                    form.change('warranty', null);
                    setError(typeof errorResponse.message === 'string' ? errorResponse.message : true);
                    if(errorResponse.helpLink) {
                        setHelpLink(errorResponse.helpLink);
                    }
                    if(errorResponse.helpLinkText) {
                        setHelpLinkText(errorResponse.helpLinkText);
                    }
                    addCompany(null);
                    addCoverageId(null);
                    setAddressId(null);
                })
        }
    })

    const error = internalError; //|| form.getState().errors?.serialnumber;

    useEffect(() => {
        if(!dsn) {
            return;
        }
        setSerialnumber(dsn);
        checkSerialnumber({ serialnumber: dsn });
    }, [dsn]);

    useEffect(() => {
        if(Array.isArray(openOrders) && openOrders.length > 0) {
            setAddress(last(openOrders).installation_address);
        }
    }, [openOrders]);


    const onEnter = (event) => {
        event.preventDefault();

        if(!values.serialnumber) {
            return;
        }

        checkSerialnumber({ serialnumber: values.serialnumber });
    };


    return (
        <div>
            <Field name="warranty">
                {({ input }) => (
                    <input {...input} type="hidden" />
                )}
            </Field>

            <div className={'flex gap-3 items-start max-w-xl'}>
                <div className={'flex-1'}>
                    <Field
                        name={'serialnumber'}
                        type={'text'}
                        component={TextInput}
                        validate={value => {
                            if(validateSerialNumber) {
                                return validateSerialNumber(value);
                            }
                            if(serialnumberRegex) {
                                let checkRegex = serialnumberRegex;
                                if (checkRegex.startsWith('/') && checkRegex.endsWith('/')) {
                                    checkRegex = checkRegex.slice(1, -1);
                                }
                                return (new RegExp(checkRegex)).test(value) ? undefined : <Trans i18nKey="serialNumber.invalid">Invalid Serial Number</Trans>;
                            }

                            return ((value || shopMode) ? undefined : <Trans i18nKey="serialNumber.required">Required</Trans>);
                        }}
                        field={{
                            label: <Trans i18nKey="serialNumber">Serial Number</Trans>,
                            type: uppercase ? 'uppercase' : undefined,
                        }}
                        onEnter={onEnter}
                        disabled={manualDeviceSelection || product || success}
                    />
                    <HelpModal />
                </div>
                {
                    !success &&
                    <MuiButton
                        variant="contained"
                        color={'secondary'}
                        onClick={onEnter}
                        id={'serialnumber-search'}
                        disabled={manualDeviceSelection || isCheckSerialnumberLoading}
                        size={'large'}
                        styled={{ padding: '14px 22px', }}
                        startIcon={isCheckSerialnumberLoading && <CircularProgress size={14} /> }
                    >
                        <Trans i18nKey="serialNumber.search">Search</Trans>
                    </MuiButton>
                }
                {
                    !!success &&
                    <MuiButton
                        variant="contained"
                        color={'secondary'}
                        onClick={() => {
                            onChangeCallback?.();
                            orderEntryDispatch({ type: 'setSerialNumber', value: {} });
                            setError(false);
                            addCompany(null);
                            addCoverageId(null);
                            setAddressId(null);
                        }}
                        id={'serialnumber-search'}
                        disabled={manualDeviceSelection}
                        size={'large'}
                        styled={{ padding: '14px 22px', }}
                        startIcon={<ChangeCircleIcon />}
                    >
                        <Trans i18nKey="serialNumber.change">Change</Trans>
                    </MuiButton>
                }
            </div>
            {
                !!error && !manualDeviceSelection && field?.meta?.touched && (
                    <div className={'flex justify-center mt-6'}>
                        <div className={'p-6 max-w-2xl b2x-shadow-md rounded-lg mt-3 flex flex-col justify-center items-center gap-1 bg-red-100 text-center'}>
                            <h3 className={'font-bold text-lg m-0'}>
                                { error }
                            </h3>
                            {
                                helpLink &&
                                <a href={helpLink} target={'_blank'} className={'underline text-black mt-1.5 text-sm'}>
                                    {
                                        helpLinkText ||
                                        (<Trans i18nKey="common.helpAmazon">Go Directly to Amazon Support</Trans>)
                                    }
                                </a>
                            }
                        </div>
                    </div>
                )
            }
            {
                !!product && !manualDeviceSelection && !user && !doNotShowConfirmDevice &&
                <div>
                    <div>
                        <h3 className={'h3 mt-9'}><Trans i18nKey="common.deviceConfirm">Confirm your device</Trans></h3>
                    </div>
                    <div className={'bg-white p-6 b2x-shadow-md rounded-lg grid grid-cols-12 mt-3 gap-1'}>
                        <div className={'col-span-12 md:col-span-3'}>
                            <div className={'px-12 py-6'}>
                                {
                                    (product.image || selectedCategory?.image) && <img className={'max-w-full '} src={product.image || selectedCategory?.image} alt={product.description} />
                                }
                            </div>
                        </div>
                        <div className={'col-span-12 md:col-span-6 flex flex-col items-start md:pl-6'}>
                            <div className={'flex-1'}>
                                <p><span className={'font-bold'}><Trans i18nKey={'common.device'}>Device</Trans>:</span> { product.productcategory?.description } </p>
                                <p><span className={'font-bold'}><Trans i18nKey="common.version">Version</Trans>:</span> { product.description } </p>
                                <p><span className={'font-bold'}><Trans i18nKey="common.serialNumberDNS">Serial Number (DNS)</Trans>:</span> { values.serialnumber } </p>
                            </div>
                        </div>
                        <div className={'col-span-12 md:col-span-3 flex items-end'}>
                            <MuiButton
                                variant="contained"
                                color={'secondary'}
                                onClick={nextStep}
                                id={'serialnumber-confirm'}
                            >
                                <Trans i18nKey={'orderEntry.confirmThisDevice'}>
                                    Confirm this device
                                </Trans>
                            </MuiButton>
                        </div>
                    </div>
                </div>
            }
            {
                values.requesttype === 'incident' && !!product && !!product.coverages &&
                <div>
                    <hr/>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={8}>
                            <h3>
                              <Trans i18nKey="serialNumber.coverage">Coverage</Trans>
                            </h3>
                            <p>
                              <Trans i18nKey="serialNumber.coverage.description">Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.</Trans>
                            </p>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        {
                            product.coverages
                                .map((item) =>
                                    <Grid item xs={12} sm={4} key={item.name}>
                                        {/*<Field*/}
                                        {/*    name={String(item.id)}*/}
                                        {/*    type={'radio'}*/}
                                        {/*    value={String(item.id)}*/}
                                        {/*    component={RadioButton}*/}
                                        {/*    field={{*/}
                                        {/*        titel: item.name,*/}
                                        {/*    }}*/}
                                        {/*    disabled={true}*/}
                                        {/*    checked={true}*/}
                                        {/*/>*/}
                                        <Field name="coverage_id" type="radio" value={item.id}>
                                            {({input}) => (
                                                <RadioButton
                                                    input={{
                                                        name: input.name,
                                                        type: 'radio',
                                                        disabled: true,
                                                        checked: input.checked,
                                                        value: item.id,
                                                        onChange: input.onChange,
                                                    }}
                                                    field={{
                                                        titel: item.name,
                                                        description: item.description,
                                                    }}
                                                    meta={{
                                                        titel: item.name,
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                )
                        }
                    </Grid>
                </div>
            }
            {
                values.requesttype === 'incident' && !!problems &&
                    <div>
                        <hr/>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <h3>
                                  <Trans i18nKey="serialNumber.problemDescription">Problem description</Trans>
                                </h3>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <p>
                                  <Trans i18nKey="serialNumber.problemDescription.description">Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.</Trans>
                                </p>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            name={'problem_1'}
                                            type={'select'}
                                            component={Select}
                                            validate={value => (value ? undefined : <Trans i18nKey="serialNumber.required">Required</Trans>)}
                                            field={{
                                                label: <Trans i18nKey="serialNumber.problem">Problem</Trans>,
                                                titel: <Trans i18nKey="serialNumber.problem.title">123</Trans>,
                                                options: problems.map(problem => ({
                                                    value: problem.id,
                                                    label: problem.name,
                                                })),
                                            }}
                                        />
                                        {
                                            currentProblem && currentProblem.problems.length > 0 &&
                                                <MarginTop>
                                                    <Field
                                                        name={'problem_2'}
                                                        type={'select'}
                                                        component={Select}
                                                        validate={value => (value ? undefined : 'Erforderlich')}
                                                        field={{
                                                            label: <Trans i18nKey="serialNumber.problem">Problem</Trans>,
                                                            titel: <Trans i18nKey="serialNumber.problem.title">123</Trans>,
                                                            options: currentProblem.problems.map(problem => ({
                                                                value: problem.id,
                                                                label: problem.name,
                                                            })),
                                                        }}
                                                    />
                                                </MarginTop>
                                        }
                                    </Grid>
                                </Grid>
                                {
                                    currentProblem && (currentProblem.problems.length === 0 || values.problem_2) &&
                                    <MarginTop>
                                        <Field
                                            name={'problem_description'}
                                            type={'text'}
                                            component={TextInput}
                                            validate={value => (value ? undefined : <Trans i18nKey="serialNumber.required">Required</Trans>)}
                                            field={{
                                                label: <Trans i18nKey="serialNumber.problemDescription">Problem description</Trans>,
                                            }}
                                        />
                                    </MarginTop>
                                }
                            </Grid>
                        </Grid>
                </div>
            }
            {
                values.requesttype === 'accessory' && !!accessories &&
                <div>
                    <hr/>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <h3>
                              <Trans i18nKey="serialNumber.availableAccessory">Available Accessory</Trans>
                            </h3>
                        </Grid>
                        <Grid item xs={12}>
                            <p>
                              <Trans i18nKey="serialNumber.availableAccessory.description">Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.</Trans>
                            </p>

                            <Grid container spacing={3}>
                                {
                                    accessories
                                        .map((item) =>
                                            <Grid item xs={12} sm={4} key={item.id}>
                                                <ProductItem>
                                                    <Field
                                                        name={'accessory_'+item.id}
                                                        type={'select'}
                                                        value={'productvariant_'+item.id}
                                                        //placeholder={field.placeholder}
                                                        component={SelectCard}
                                                        // validate={value => (value ? undefined : 'Erforderlich')}
                                                        field={{
                                                            label: item.description,
                                                            description: <Trans i18nKey="serialNumber.accessoryField.description">Lorem ipsum dolor sit amet</Trans>,
                                                            titel: item.description,
                                                            options: Array.from(Array(100).keys()).map((variant, index) => ({
                                                                value: index,
                                                                label: index,
                                                            })),
                                                        }}
                                                        defaultValue={0}
                                                    />
                                                </ProductItem>
                                            </Grid>,
                                        )
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            }
            {
                !!openOrders && openOrders.length > 0 &&
                <div style={{ marginTop: 30, marginBottom: 30 }}>
                    <TableContainer>
                        <h3><Trans i18nKey="queueDetail.orderHistory">Order history</Trans></h3>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell><Trans i18nKey="queueDetail.history.date">Date</Trans></TableCell>
                                    <TableCell><Trans i18nKey="queueDetail.serialNumber">Serial number</Trans></TableCell>
                                    <TableCell><Trans i18nKey="queueDetail.status">Status</Trans></TableCell>
                                    <TableCell><Trans i18nKey="queueDetail.product">Product</Trans></TableCell>
                                    <TableCell><Trans i18nKey="queueDetail.address">Address</Trans></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {openOrders.map((row) =>
                                    (
                                        <TableRow key={row.id}>
                                            <TableCell>
                                                {dayjs(row.created_at).format('DD.MM.YYYY HH:mm')}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {
                                                    row.orderdetails.map(item => item.serialnumber).join('\n')
                                                }
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {
                                                    row.status?.name
                                                }
                                            </TableCell>
                                            <TableCell style={{whiteSpace: 'pre-wrap'}}>
                                                {
                                                    row.orderdetails.map(item => item.product.description).join('\n')
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {row.installation_address?.street} <br/>
                                                {row.installation_address?.zip} {row.installation_address?.city} <br/>
                                                {row.installation_address?.email}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            }
        </div>
    );
},
function areEqual(prevProps, nextProps) {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
});

export default SerialNumber;
